import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { GridBooleanColumnConfig } from '../../components/paged-grid/models/grid-boolean-column.config';
import { GridColumnConfig } from '../../components/paged-grid/models/grid-column.config';
import { GridDateColumnConfig } from '../../components/paged-grid/models/grid-date-column.config';
import { GridNumberColumnConfig } from '../../components/paged-grid/models/grid-number-column.config';
import { GridConfig } from '../../components/paged-grid/models/grid.config';
import { ModelTypes } from '../../core/utils/mappers/model.types';
import { ListShareholderModel } from '../../models/list-shareholder.model';
import { ShareholdersResource } from '../../services/resources/shareholders.resource';

@Component({
    selector: 'sl-shareholders',
    templateUrl: './shareholders.component.html',
    styleUrls: ['./shareholders.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ShareholdersComponent {
    gridConfig: GridConfig<ListShareholderModel>;

    private readonly shareholdersResource = inject(ShareholdersResource);

    constructor() {
        const columns = [
            new GridNumberColumnConfig({ fieldName: 'id', header: 'General.Id' }),
            new GridColumnConfig({ fieldName: 'displayName', header: 'Contact.DisplayName' }),
            new GridColumnConfig({ fieldName: 'locationZipCode', header: 'Address.ZipCode', isHidden: true }),
            new GridColumnConfig({ fieldName: 'locationCity', header: 'Address.City' }),
            new GridColumnConfig({ fieldName: 'locationCountryName', header: 'Address.LocationCountryName', isHidden: true }),
            new GridColumnConfig({
                fieldName: 'locationContinentName',
                header: 'Address.Continent',
                isHidden: false
            }),
            new GridColumnConfig({ fieldName: 'languageId', header: 'General.Language', isHidden: true }),
            new GridBooleanColumnConfig({ fieldName: 'isConfidential', header: 'Shareholders.IsConfidential', isHidden: true }),
            new GridColumnConfig({ fieldName: 'companyName', header: 'Shareholders.CompanyName', isHidden: true }),
            new GridColumnConfig({ fieldName: 'companyDepartment', header: 'Shareholders.CompanyDepartament', isHidden: true }),
            new GridBooleanColumnConfig({ fieldName: 'companyNameFirst', header: 'Shareholders.CompanyNameFirst', isHidden: true }),
            new GridColumnConfig({ fieldName: 'firstName', header: 'Contact.FirstName', isHidden: true }),
            new GridColumnConfig({ fieldName: 'firstNameSupplement', header: 'Contact.FirstNameSupplement', isHidden: true }),
            new GridColumnConfig({ fieldName: 'lastName', header: 'Contact.LastName', isHidden: true }),
            new GridColumnConfig({ fieldName: 'lastNameSupplement', header: 'Contact.LastNameSupplement', isHidden: true }),
            new GridBooleanColumnConfig({ fieldName: 'hasHyphen', header: 'Contact.HasHyphen', isHidden: true }),
            new GridColumnConfig({ fieldName: 'jobTitle', header: 'Shareholders.JobTitle', isHidden: true }),
            new GridColumnConfig({ fieldName: 'street', header: 'Address.Street', isHidden: true }),
            new GridColumnConfig({ fieldName: 'streetSupplement', header: 'Address.StreetSupplement', isHidden: true }),
            new GridColumnConfig({ fieldName: 'streetNumber', header: 'Address.StreetNumber', isHidden: true }),
            new GridColumnConfig({ fieldName: 'poBox', header: 'Address.PoBoxNr', isHidden: true }),
            new GridColumnConfig({ fieldName: 'email', header: 'Contact.Email', isHidden: true }),
            new GridDateColumnConfig({ fieldName: 'dateOfBirth', header: 'General.DateOfBirth', isHidden: true }),
            new GridColumnConfig({ fieldName: 'nationalityCountryName', header: 'Contact.Nationality', isHidden: true }),
            new GridBooleanColumnConfig({
                fieldName: 'isManagementOrBoardOfDirectors',
                header: 'Shareholders.IsManagementOrBoardOfDirectors',
                isHidden: true
            }),
            new GridBooleanColumnConfig({ fieldName: 'isEmployee', header: 'Shareholders.IsEmployee', isHidden: true }),
            new GridColumnConfig({ fieldName: 'employeeNumber', header: 'Shareholders.EmployeeNumber', isHidden: true }),
            new GridDateColumnConfig({
                fieldName: 'correspondenceValidFromDate',
                header: 'Shareholders.CorrespondenceValidFromDate',
                isHidden: true
            }),
            new GridBooleanColumnConfig({ fieldName: 'dividendRight', header: 'Shareholders.DividendRight', isHidden: true }),
            new GridBooleanColumnConfig({ fieldName: 'subscriptionRight', header: 'Shareholders.SubscriptionRight', isHidden: true }),
            new GridColumnConfig({
                fieldName: 'remarks',
                header: 'General.Remarks',
                isHidden: true,
                requiredPermission: 'GridColumnShareholderRemarks'
            }),
            new GridColumnConfig({ fieldName: 'externalId', header: 'Shareholders.ExternalId', isHidden: true }),
            new GridBooleanColumnConfig({
                fieldName: 'streetAfterSupplement',
                header: 'Address.StreetAfterSupplement',
                isHidden: true
            }),
            new GridBooleanColumnConfig({
                fieldName: 'hasMoneyLaunderingActDossier',
                header: 'Shareholders.HasMoneyLaunderingActDossier',
                isHidden: true
            }),
            new GridBooleanColumnConfig({
                fieldName: 'isPoliticallyExposedPerson',
                header: 'Contact.IsPoliticallyExposedPerson',
                isHidden: true
            }),
            new GridBooleanColumnConfig({
                fieldName: 'isIncreasedBusinessRisk',
                header: 'Shareholders.IsIncreasedBusinessRisk',
                isHidden: true
            }),
            new GridColumnConfig({ fieldName: 'mobileNumber', header: 'Contact.MobileNumber', isHidden: true }),
            new GridBooleanColumnConfig({ fieldName: 'emailConfirmed', header: 'Contact.IsEmailConfirmed', isHidden: true }),
            new GridBooleanColumnConfig({
                fieldName: 'mobileNumberConfirmed',
                header: 'Contact.IsMobileNumberConfirmed',
                isHidden: true
            }),
            new GridColumnConfig({ fieldName: 'shareholderTypeDescription', header: 'Shareholders.Type' }),
            new GridNumberColumnConfig({ fieldName: 'numberOfShares', header: 'Shareholders.NumberOfShares' }),
            new GridNumberColumnConfig({ fieldName: 'votes', header: 'Shareholders.Votes' }),
            new GridColumnConfig({ fieldName: 'ownershipTypeDescription', header: 'Shareholders.OwnershipType', isHidden: true }),
            new GridColumnConfig({
                fieldName: 'companyLegalFormDescription',
                header: 'Shareholders.LegalForm',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'companyActivity',
                header: 'Shareholders.CompanyActivity',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'salutationShort',
                header: 'Shareholders.ShortSalutation',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'salutationTitle',
                header: 'Shareholders.SalutationTitle',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'letterSalutation',
                header: 'Shareholders.LetterSalutation',
                isHidden: true
            }),
            new GridColumnConfig({ fieldName: 'deliveryMethodName', header: 'Address.DeliveryMethodName', isHidden: true }),
            new GridBooleanColumnConfig({ fieldName: 'hasCorrespondenceAddress', header: 'Correspondence.Has', isHidden: true }),
            new GridBooleanColumnConfig({ fieldName: 'correspondenceHasHyphen', header: 'Correspondence.HasHyphen', isHidden: true }),
            new GridColumnConfig({ fieldName: 'correspondenceCompanyName', header: 'Correspondence.CompanyName', isHidden: true }),
            new GridColumnConfig({
                fieldName: 'correspondenceCompanyDepartment',
                header: 'Correspondence.CompanyDepartament',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'correspondenceSalutationShort',
                header: 'Correspondence.ShortSalutation',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'correspondenceSalutationTitle',
                header: 'Correspondence.SalutationTitle',
                isHidden: true
            }),
            new GridColumnConfig({ fieldName: 'correspondenceFirstName', header: 'Correspondence.FirstName', isHidden: true }),
            new GridColumnConfig({
                fieldName: 'correspondenceFirstNameSupplement',
                header: 'Correspondence.FirstNameSupplement',
                isHidden: true
            }),
            new GridColumnConfig({ fieldName: 'correspondenceLastName', header: 'Correspondence.LastName', isHidden: true }),
            new GridColumnConfig({
                fieldName: 'correspondenceLastNameSupplement',
                header: 'Correspondence.LastNameSupplement',
                isHidden: true
            }),
            new GridColumnConfig({ fieldName: 'correspondenceJobTitle', header: 'Correspondence.JobTitle', isHidden: true }),
            new GridColumnConfig({ fieldName: 'correspondenceStreet', header: 'Correspondence.Street', isHidden: true }),
            new GridColumnConfig({
                fieldName: 'correspondenceStreetSupplement',
                header: 'Correspondence.StreetSupplement',
                isHidden: true
            }),
            new GridBooleanColumnConfig({
                fieldName: 'correspondenceStreetAfterSupplement',
                header: 'Correspondence.StreetAfterSupplement',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'correspondenceStreetNumber',
                header: 'Correspondence.StreetNumber',
                isHidden: true
            }),
            new GridColumnConfig({ fieldName: 'correspondencePoBox', header: 'Correspondence.PoBox', isHidden: true }),
            new GridColumnConfig({
                fieldName: 'correspondenceLocationZipCode',
                header: 'Correspondence.ZipCode',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'correspondenceLocationCity',
                header: 'Correspondence.City',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'correspondenceLocationCountryName',
                header: 'Correspondence.Country',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'correspondenceLocationContinentName',
                header: 'Correspondence.Continent',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'correspondenceDeliveryMethodName',
                header: 'Correspondence.DeliveryMethod',
                isHidden: true
            }),
            new GridColumnConfig({
                fieldName: 'netvoteUser',
                header: 'Shareholders.NetvoteUser',
                isHidden: true,
                requiredPermission: 'GridColumnNetvoteCredentials'
            }),
            new GridColumnConfig({
                fieldName: 'netvotePassword',
                header: 'Shareholders.NetvotePassword',
                isHidden: true,
                requiredPermission: 'GridColumnNetvoteCredentials'
            })
        ];

        this.gridConfig = new GridConfig<ListShareholderModel>({
            name: 'shareholders',
            typeName: ModelTypes.shareholders,
            resource: this.shareholdersResource,
            pageSize: 10,
            hasNewButton: true,
            hasEditButton: true,
            hasViewButton: true,
            hasRemoveButton: true,
            canExport: true,
            columns
        });
    }
}
